import { format, parse, isValid, set } from 'date-fns';

// Wire (stored/API) formats — MUST match the native inputs they replace so
// migration is byte-identical (zero backend change).
export const WIRE = {
  date: 'yyyy-MM-dd',
  time: 'HH:mm',
  datetime: "yyyy-MM-dd'T'HH:mm",
};

export function baseKind(mode) {
  if (mode === 'time') return 'time';
  if (mode === 'date' || mode === 'date-range') return 'date';
  return 'datetime'; // 'datetime' | 'datetime-range'
}

export function isRange(mode) {
  return mode === 'date-range' || mode === 'datetime-range';
}
export function hasDate(mode) {
  const k = baseKind(mode);
  return k === 'date' || k === 'datetime';
}
export function hasTime(mode) {
  const k = baseKind(mode);
  return k === 'time' || k === 'datetime';
}

// Parse a wire string into a local Date (no timezone conversion — local wall clock).
export function parseWire(str, mode) {
  if (!str || typeof str !== 'string') return null;
  const k = baseKind(mode);
  let d;
  try {
    if (k === 'time') d = parse(str, 'HH:mm', new Date());
    else if (k === 'date') d = parse(str, 'yyyy-MM-dd', new Date());
    else {
      // tolerate an optional trailing :ss that some payloads carry
      const trimmed = str.length > 16 ? str.slice(0, 16) : str;
      d = parse(trimmed, "yyyy-MM-dd'T'HH:mm", new Date());
    }
  } catch {
    return null;
  }
  return isValid(d) ? d : null;
}

// Format a Date back into the wire string for onChange / API.
export function formatWire(date, mode) {
  if (!date || !isValid(date)) return '';
  return format(date, WIRE[baseKind(mode)]);
}

// AU user-facing display for the closed field.
export function formatDisplay(date, mode) {
  if (!date || !isValid(date)) return '';
  const k = baseKind(mode);
  if (k === 'time') return format(date, 'hh:mm a');
  if (k === 'date') return format(date, 'dd/MM/yyyy');
  return format(date, 'dd/MM/yyyy, hh:mm a');
}

// Readable summary line ("Fri, 14 Aug 2026 · 5:45 PM").
export function formatSummary(date, mode) {
  if (!date || !isValid(date)) return '';
  const k = baseKind(mode);
  if (k === 'time') return format(date, 'hh:mm a');
  if (k === 'date') return format(date, 'EEE, dd MMM yyyy');
  return format(date, 'EEE, dd MMM yyyy · hh:mm a');
}

export function placeholderFor(mode) {
  const k = baseKind(mode);
  if (k === 'time') return '--:-- --';
  if (k === 'date') return 'dd/mm/yyyy';
  return 'dd/mm/yyyy, --:-- --';
}

// BS3-006: read a typed time the way people type it. Accepts "9:30 am", "9:30pm", "10:30a", "5p", "9.30", "930",
// "0930", "1730", "17:30", "9", "9am". Returns { h, m } (24-hour) or null when it cannot be read yet — e.g. "10:3"
// (minutes half typed) or "25:00". The caller keeps the text and shows a hint instead of jumping back.
export function parseTypedTime(input) {
  if (input == null) return null;
  const s = String(input).trim().toLowerCase().replace(/\s+/g, '').replace(/\./g, (m, i, all) => (/\d\.\d/.test(all.slice(i - 1, i + 2)) ? ':' : ''));
  const m = /^(\d{1,4})(?::(\d{2}))?(a|am|p|pm)?$/.exec(s);
  if (!m) return null;
  let digits = m[1];
  let minutes = m[2];
  if (minutes === undefined) {
    if (digits.length <= 2) { minutes = '00'; }               // "9", "17"
    else { minutes = digits.slice(-2); digits = digits.slice(0, -2); }   // "930" → 9:30, "1730" → 17:30
  } else if (digits.length > 2) {
    return null;
  }
  let h = Number(digits);
  const min = Number(minutes);
  if (min > 59) return null;
  const ap = m[3] ? m[3][0] : null;
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === 'a') h = h === 12 ? 0 : h;
    else h = h === 12 ? 12 : h + 12;
  } else if (h > 23) {
    return null;
  }
  return { h, m: min };
}

// Combine a date part and a time part into one Date (used when a datetime
// draft edits date and time separately).
export function mergeDateAndTime(datePart, timePart) {
  const d = datePart && isValid(datePart) ? datePart : new Date();
  const t = timePart && isValid(timePart) ? timePart : d;
  return set(d, { hours: t.getHours(), minutes: t.getMinutes(), seconds: 0, milliseconds: 0 });
}
